/**
 * Helpers to manipulate dates for the planning calendar.
 * Week starts on Monday. ISO-week-based.
 */

const MS_DAY = 86_400_000;

export const TYPE_COLORS = {
  meeting: "bg-blue-100 text-blue-800 border-blue-200",
  collab: "bg-violet-100 text-violet-800 border-violet-200",
  post: "bg-emerald-100 text-emerald-800 border-emerald-200",
  live: "bg-rose-100 text-rose-800 border-rose-200",
  custom: "bg-slate-100 text-slate-700 border-slate-200",
};

export const TYPE_BORDER = {
  meeting: "border-blue-400",
  collab: "border-violet-400",
  post: "border-emerald-400",
  live: "border-rose-400",
  custom: "border-slate-400",
};

export function startOfDay(d) {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

export function startOfWeek(d) {
  const c = startOfDay(d);
  const dow = (c.getDay() + 6) % 7; // Monday = 0
  c.setDate(c.getDate() - dow);
  return c;
}

export function endOfWeek(d) {
  const c = startOfWeek(d);
  c.setDate(c.getDate() + 7);
  return c;
}

export function startOfMonth(d) {
  const c = new Date(d.getFullYear(), d.getMonth(), 1);
  c.setHours(0, 0, 0, 0);
  return c;
}

export function endOfMonth(d) {
  const c = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  c.setHours(0, 0, 0, 0);
  return c;
}

/**
 * Returns the 42-day grid (6 weeks) starting on the Monday that contains
 * the first day of the month containing `d`.
 */
export function monthGrid(d) {
  const start = startOfWeek(startOfMonth(d));
  const out = [];
  for (let i = 0; i < 42; i++) {
    out.push(new Date(start.getTime() + i * MS_DAY));
  }
  return out;
}

export function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function fmtDayLabel(d, locale) {
  return d.toLocaleDateString(locale, { weekday: "short", day: "numeric" });
}

export function fmtMonthYear(d, locale) {
  return d.toLocaleDateString(locale, { month: "long", year: "numeric" });
}

export function fmtTime(iso, locale) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtRange(startIso, endIso, locale) {
  return `${fmtTime(startIso, locale)} → ${fmtTime(endIso, locale)}`;
}

/** Build an ISO datetime-local string suitable for <input type="datetime-local"> */
export function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

export function fromLocalInput(value) {
  if (!value) return null;
  return new Date(value).toISOString();
}

export function groupByDay(events) {
  const m = new Map();
  events.forEach((e) => {
    const k = startOfDay(new Date(e.start_at)).toISOString();
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(e);
  });
  // Sort each bucket by start_at
  for (const arr of m.values())
    arr.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  return m;
}
